import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { adminAPI } from '../services/api';

const CartContext = createContext(null);

// Admin-only: holds the org's current plan-request cart (multiple tiers +
// quantities an admin has "added to cart") so the Navbar's cart widget and
// the Billing page both read the exact same state and update together
// without a page reload — one fetch, shared via context, refetched after
// every mutation (add/remove/checkout/request-discount).
export const CartProvider = ({ children }) => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [billing, setBilling] = useState(null);
  const [loading, setLoading] = useState(isAdmin);

  const refresh = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const res = await adminAPI.getBilling();
      setBilling(res.data.data.billing);
    } catch {
      // Silent — the Billing page itself surfaces load failures with a toast;
      // the Navbar widget just stays empty rather than erroring loudly.
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => { refresh(); }, [refresh]);

  const cartItems = billing?.cart_items || [];

  const value = {
    isAdmin,
    loading,
    billing,
    cartItems,
    cartSubtotal: billing?.cart_subtotal || 0,
    cartTotal: billing?.cart_total || 0,
    discountPercent: Number(billing?.discount_percent) || 0,
    discountRequested: !!billing?.cart_discount_requested,
    discountApproved: !!billing?.cart_discount_approved,
    refresh,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used within a CartProvider');
  return ctx;
};
